import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, Save, Library, MessageSquare, X, Wrench, Send, ChevronDown, ChevronUp, RotateCcw, Users, Calendar, AlertTriangle, CheckCircle2 } from "lucide-react";
import BulkApplyOverrideModal from "@/components/BulkApplyOverrideModal";

const PHASE_LABELS = {
  warm_engine:    "1. Warm the Engine",
  lower_prep:     "2. Lower-Body Prep",
  upper_prep:     "3. Upper-Body Prep",
  arm_activation: "4. Arm Activation",
  plyo_throws:    "5. PlyoCare Throws",
  custom:         "Custom",
};
const PHASES = ["warm_engine", "lower_prep", "upper_prep", "arm_activation", "plyo_throws", "custom"];
const TOOL_LABELS = {
  band: "Bands", plyo: "Plyo", j_band: "J-Band",
  weighted_ball: "Weighted Ball", water_stability: "Water Stability",
};
const TOOLS = ["band", "plyo", "j_band", "weighted_ball", "water_stability"];

function today() { return new Date().toISOString().slice(0, 10); }

export default function RoutineOverrideBuilder({ playerId, playerName }) {
  const [day, setDay] = useState(today());
  const [override, setOverride] = useState(null);
  const [library, setLibrary] = useState({ defaults: [], team: [] });
  const [suggestions, setSuggestions] = useState({ findings: [], suggestions: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showLib, setShowLib] = useState(false);
  const [showNotes, setShowNotes] = useState(true);
  const [showBulk, setShowBulk] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: ov }, { data: lib }, { data: sug }] = await Promise.all([
        api.get(`/players/${playerId}/routine-override?date=${day}`),
        api.get(`/team/corrective-library`),
        api.get(`/players/${playerId}/suggested-correctives`).catch(() => ({ data: { findings: [], suggestions: [] } })),
      ]);
      setOverride(ov.override || { date: day, base_protocol: "pre_throw", blocks: [], notes: [] });
      setLibrary(lib);
      setSuggestions(sug);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [playerId, day]);

  const blocks = override?.blocks || [];
  const notes  = override?.notes  || [];

  const addBlock = (preset = {}) => {
    const newBlock = {
      block_id: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      source: preset.source || "custom",
      phase: preset.phase || "custom",
      title: preset.title || "New corrective block",
      description: preset.description || "",
      sets: preset.sets ?? null,
      reps: preset.reps ?? null,
      duration_sec: preset.duration_sec ?? null,
      video_url: preset.video_url || "",
      tool_tags: preset.tool_tags || [],
      notes: "",
    };
    setOverride((o) => ({ ...o, blocks: [...(o?.blocks || []), newBlock] }));
  };

  const updateBlock = (idx, patch) => {
    setOverride((o) => ({ ...o, blocks: o.blocks.map((b, i) => (i === idx ? { ...b, ...patch } : b)) }));
  };

  const removeBlock = (idx) => {
    setOverride((o) => ({ ...o, blocks: o.blocks.filter((_, i) => i !== idx) }));
  };

  const move = (idx, dir) => {
    const next = [...blocks];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setOverride((o) => ({ ...o, blocks: next }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        date: day,
        base_protocol: override?.base_protocol || "pre_throw",
        blocks: blocks.map(({ block_id, ...rest }) => ({ block_id: block_id?.startsWith("tmp_") ? undefined : block_id, ...rest })),
      };
      const { data } = await api.put(`/players/${playerId}/routine-override`, payload);
      setOverride(data.override);
      toast.success("Override saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!confirm("Reset to platform baseline (delete this override)?")) return;
    try {
      await api.delete(`/players/${playerId}/routine-override?date=${day}`);
      toast.success("Override cleared");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addNote = async () => {
    const body = noteDraft.trim();
    if (!body) return;
    try {
      const { data } = await api.post(`/players/${playerId}/protocol-notes`, { date: day, body });
      setOverride((o) => ({ ...(o || {}), notes: data.notes }));
      setNoteDraft("");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const allLibrary = useMemo(() => [
    ...library.defaults.map((d) => ({ ...d, _src: "platform" })),
    ...library.team.map((d) => ({ ...d, _src: "team" })),
  ], [library]);

  if (loading) return <div className="text-sm text-white/50">Loading routine builder…</div>;

  const inputCls = "bg-[var(--ink-1)] border-[var(--ink-border)] text-white";
  const assessment = suggestions.assessment || {};

  // 3-month re-screening reminder
  const rescreen = assessment.rescreen_status;
  const days = assessment.days_since_assessment;
  const rescreenStyle = {
    current:  { bg: "rgba(16,185,129,0.08)", border: "rgba(16,185,129,0.35)", fg: "#34D399", icon: CheckCircle2, label: "Up to date" },
    due_soon: { bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.40)", fg: "#FBBF24", icon: AlertTriangle, label: "Re-screen due soon" },
    overdue:  { bg: "rgba(220,38,38,0.10)",  border: "rgba(220,38,38,0.45)",  fg: "#FCA5A5", icon: AlertTriangle, label: "Re-screen overdue" },
    never:    { bg: "rgba(34,211,238,0.06)", border: "rgba(34,211,238,0.30)", fg: "#22D3EE", icon: AlertTriangle, label: "No assessment logged" },
  }[rescreen] || null;

  return (
    <div className="space-y-4" data-testid="routine-override-builder">
      {/* Per-athlete prescription banner */}
      <div className="rounded-xl p-4 border" style={{ backgroundColor: "rgba(34,211,238,0.05)", borderColor: "rgba(34,211,238,0.25)" }}>
        <div className="flex items-start gap-3">
          <Wrench className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: "#22D3EE" }} />
          <div className="flex-1">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: "#22D3EE" }}>Individual prescription</div>
            <div className="font-display font-bold text-sm text-white mt-0.5">
              You are prescribing for <b className="text-cyan-300">{playerName}</b> only.
              Use the Library&apos;s <b className="text-white">Suggested for {playerName}</b> panel for findings-driven correctives, then optionally <b className="text-white">Apply to many</b> to athletes with shared findings.
            </div>
          </div>
        </div>
      </div>

      {/* Re-screening reminder (3-month cadence) */}
      {rescreenStyle && (
        <div className="rounded-xl p-3 border flex items-center justify-between gap-3"
          style={{ backgroundColor: rescreenStyle.bg, borderColor: rescreenStyle.border }}
          data-testid={`rescreen-banner-${rescreen}`}>
          <div className="flex items-center gap-3 min-w-0">
            <rescreenStyle.icon className="h-4 w-4 flex-shrink-0" style={{ color: rescreenStyle.fg }} />
            <div className="text-xs min-w-0">
              <span className="font-bold uppercase tracking-wider text-[10px]" style={{ color: rescreenStyle.fg }}>
                {rescreenStyle.label}
              </span>
              <span className="text-white/70 ml-2">
                {days !== null && days !== undefined
                  ? <>Last assessed <b className="text-white">{days} day{days === 1 ? "" : "s"}</b> ago{assessment.last_assessed_by_name ? <> by <b className="text-white/85">{assessment.last_assessed_by_name}</b></> : null}.</>
                  : <>No FMS / GIRD / Scap UR / Ortho data on file yet.</>}
                {" "}ArmIQ recommends re-screening every <b className="text-white">3 months</b>.
              </span>
            </div>
          </div>
          <Button onClick={() => {
            const tab = document.querySelector('[data-testid="tab-baseline"]');
            if (tab) tab.click();
          }} size="sm" variant="outline"
            className="border-white/15 text-white bg-white/5 hover:bg-white/10 whitespace-nowrap"
            data-testid="rescreen-cta">
            <Calendar className="h-3.5 w-3.5 mr-1" /> Log assessment
          </Button>
        </div>
      )}

      <div className="rounded-xl ink-card p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase flex items-center gap-1.5">
              <Wrench className="h-3 w-3" /> Routine Override · {playerName}
            </div>
            <div className="font-display font-bold text-lg text-white mt-1">
              {blocks.length === 0 ? "Using platform baseline routine" : `${blocks.length} custom block${blocks.length === 1 ? "" : "s"} active`}
            </div>
            {override?.updated_by_name && (
              <div className="text-[11px] text-white/45 mt-0.5">
                Last edited by <b className="text-white/70">{override.updated_by_name}</b> ({override.updated_by_role}) ·{" "}
                {new Date(override.updated_at).toLocaleString()}
                {override.bulk_apply_count && override.bulk_apply_count > 1 && (
                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
                    style={{ backgroundColor: "rgba(34,211,238,0.15)", color: "#67E8F9", border: "1px solid rgba(34,211,238,0.35)" }}
                    data-testid="bulk-applied-badge">
                    Team-wide · {override.bulk_apply_count} athletes
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="ovr-date" className="text-xs text-white/60">Date</Label>
            <Input id="ovr-date" type="date" value={day} onChange={(e) => setDay(e.target.value)}
              className={`h-9 w-40 ${inputCls}`} data-testid="override-date" />
            <Button onClick={() => setShowLib((v) => !v)} variant="outline"
              className="border-white/15 text-white bg-white/5 hover:bg-white/10 h-9"
              data-testid="toggle-corrective-library">
              <Library className="h-4 w-4 mr-1" /> Library
            </Button>
            <Button onClick={() => addBlock()} variant="outline"
              className="border-white/15 text-white bg-white/5 hover:bg-white/10 h-9"
              data-testid="add-custom-block">
              <Plus className="h-4 w-4 mr-1" /> Add block
            </Button>
            <Button onClick={() => setShowBulk(true)} disabled={blocks.length === 0} variant="outline"
              className="border-cyan-400/40 text-cyan-200 bg-cyan-400/10 hover:bg-cyan-400/20 h-9 disabled:opacity-40"
              data-testid="open-bulk-apply">
              <Users className="h-4 w-4 mr-1" /> Apply to many
            </Button>
            <Button onClick={reset} variant="outline"
              className="border-white/15 text-white bg-white/5 hover:bg-white/10 h-9"
              data-testid="reset-override">
              <RotateCcw className="h-4 w-4 mr-1" /> Reset
            </Button>
            <Button onClick={save} disabled={saving}
              className="text-white font-bold red-glow h-9"
              style={{ backgroundColor: "var(--brand-secondary)" }}
              data-testid="save-override">
              <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </div>

      {/* Corrective Library drawer */}
      {showLib && (
        <div className="rounded-xl ink-card p-4" data-testid="corrective-library-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Corrective Library — tap to add</div>
            <button onClick={() => setShowLib(false)} className="text-white/50 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Suggested for this athlete — based on assessment data */}
          {suggestions.suggestions.length > 0 && (
            <div className="mb-4 rounded-lg p-3 border" style={{ backgroundColor: "rgba(34,211,238,0.06)", borderColor: "rgba(34,211,238,0.25)" }}
              data-testid="suggested-correctives">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] tracking-[0.25em] font-bold uppercase" style={{ color: "#22D3EE" }}>
                  Suggested for {playerName} · {suggestions.suggestions.length} match{suggestions.suggestions.length === 1 ? "" : "es"}
                </div>
                <div className="flex flex-wrap gap-1 max-w-[60%] justify-end">
                  {suggestions.findings.map((f) => (
                    <span key={f.id} className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: "rgba(220,38,38,0.15)", color: "#FCA5A5", border: "1px solid rgba(220,38,38,0.30)" }}>
                      {f.label}
                    </span>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {suggestions.suggestions.map((c) => (
                  <button key={`sug-${c.id}`} type="button" onClick={() => addBlock(c)}
                    className="text-left rounded-lg p-3 border bg-white/[0.03] hover:bg-cyan-400/10 transition"
                    style={{ borderColor: "rgba(34,211,238,0.40)" }}
                    data-testid={`suggested-item-${c.id}`}>
                    <div className="font-bold text-white text-sm leading-tight">{c.title}</div>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {c.reasons.map((r, i) => (
                        <span key={i} className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full"
                          style={{ backgroundColor: "rgba(34,211,238,0.15)", color: "#67E8F9" }}>
                          {r}
                        </span>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {suggestions.suggestions.length === 0 && suggestions.findings.length === 0 && (
            <div className="mb-3 text-[11px] text-white/45 italic px-1">
              💡 Log this athlete&apos;s assessment data on the <b className="text-white/70">Baseline</b> tab (FMS · GIRD · Scap UR · Ortho tests) — the engine will then auto-suggest the matching correctives here.
            </div>
          )}

          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40 mb-2">All correctives</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {allLibrary.map((c) => (
              <button
                key={`${c._src}-${c.id}`}
                type="button"
                onClick={() => addBlock(c)}
                className="text-left rounded-lg p-3 border border-white/10 bg-white/[0.03] hover:border-cyan-400/50 hover:bg-cyan-400/5 transition"
                data-testid={`library-item-${c.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="font-bold text-white text-sm leading-tight">{c.title}</div>
                  <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${c._src === "team" ? "bg-amber-400/15 text-amber-300" : "bg-cyan-400/15 text-cyan-300"}`}>
                    {c._src}
                  </span>
                </div>
                <div className="text-[10px] text-white/50 mt-1">{PHASE_LABELS[c.phase] || c.phase}</div>
                <div className="text-[10px] text-white/60 mt-1 line-clamp-2">{c.description}</div>
                {c.tool_tags?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {c.tool_tags.map((t) => (
                      <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-white/60 border border-white/10">
                        {TOOL_LABELS[t]}
                      </span>
                    ))}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Blocks list */}
      <div className="space-y-2">
        {blocks.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/10 p-8 text-center text-sm text-white/40">
            No custom blocks yet. The athlete will see the platform&apos;s default pre-throwing routine.
            Tap <b className="text-white/70">Library</b> or <b className="text-white/70">Add block</b> to override.
          </div>
        ) : blocks.map((b, idx) => (
          <BlockRow key={b.block_id} idx={idx} block={b}
            onChange={(p) => updateBlock(idx, p)}
            onMove={(d) => move(idx, d)}
            onRemove={() => removeBlock(idx)}
            inputCls={inputCls}
            isFirst={idx === 0} isLast={idx === blocks.length - 1}
          />
        ))}
      </div>

      {/* Threaded notes */}
      <div className="rounded-xl ink-card p-4">
        <button onClick={() => setShowNotes((v) => !v)} className="w-full flex items-center justify-between" data-testid="notes-toggle">
          <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase flex items-center gap-1.5">
            <MessageSquare className="h-3 w-3" /> Staff notes — {notes.length}
          </div>
          {showNotes ? <ChevronUp className="h-4 w-4 text-white/40" /> : <ChevronDown className="h-4 w-4 text-white/40" />}
        </button>
        {showNotes && (
          <>
            <div className="mt-3 space-y-2">
              {notes.length === 0 && (
                <div className="text-xs text-white/40 italic">No notes yet. All staff (Coach · ATC · S&amp;C) can append.</div>
              )}
              {notes.map((n) => (
                <div key={n.note_id} className="rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2" data-testid={`note-${n.note_id}`}>
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="font-bold text-white">{n.author_name}</span>
                    <span className="text-white/40 font-mono-num">{new Date(n.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-[10px] tracking-wider uppercase font-bold mt-0.5"
                       style={{ color: n.author_subrole === "athletic_trainer" ? "#34D399" :
                                       n.author_subrole === "strength_conditioning" ? "#FBBF24" :
                                       n.author_subrole === "head" ? "#FCA5A5" : "#22D3EE" }}>
                    {(n.author_subrole || n.author_role || "staff").replace(/_/g, " ")}
                  </div>
                  <div className="text-sm text-white/85 mt-1.5 leading-relaxed whitespace-pre-wrap">{n.body}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-end gap-2">
              <Textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="Add a note (visible to all staff)…"
                className={`min-h-[60px] flex-1 ${inputCls}`}
                data-testid="note-draft"
              />
              <Button onClick={addNote} disabled={!noteDraft.trim()}
                className="h-10 text-white font-bold" style={{ backgroundColor: "var(--brand-secondary)" }}
                data-testid="add-note">
                <Send className="h-4 w-4 mr-1" /> Post
              </Button>
            </div>
          </>
        )}
      </div>

      <BulkApplyOverrideModal
        open={showBulk}
        onClose={() => setShowBulk(false)}
        currentPlayerId={playerId}
        blocks={blocks}
        day={day}
        baseProtocol={override?.base_protocol || "pre_throw"}
        onApplied={() => load()}
      />
    </div>
  );
}

function BlockRow({ idx, block, onChange, onMove, onRemove, inputCls, isFirst, isLast }) {
  return (
    <div className="rounded-xl ink-card p-4" data-testid={`block-${idx}`}>
      <div className="grid grid-cols-[auto_1fr_auto] gap-3">
        {/* Move handle */}
        <div className="flex flex-col items-center gap-0.5 pt-2">
          <button onClick={() => onMove(-1)} disabled={isFirst} className="text-white/40 hover:text-white disabled:opacity-30">
            <ChevronUp className="h-4 w-4" />
          </button>
          <GripVertical className="h-4 w-4 text-white/30" />
          <button onClick={() => onMove(1)} disabled={isLast} className="text-white/40 hover:text-white disabled:opacity-30">
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
        {/* Body */}
        <div className="space-y-2">
          <Input value={block.title} onChange={(e) => onChange({ title: e.target.value })}
            className={`text-base font-bold ${inputCls}`} placeholder="Block title" data-testid={`block-${idx}-title`} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <select value={block.phase} onChange={(e) => onChange({ phase: e.target.value })}
              className={`h-9 rounded-md px-2 text-sm ${inputCls}`} data-testid={`block-${idx}-phase`}>
              {PHASES.map((p) => <option key={p} value={p}>{PHASE_LABELS[p]}</option>)}
            </select>
            <Input type="number" min={0} value={block.sets ?? ""} onChange={(e) => onChange({ sets: e.target.value ? Number(e.target.value) : null })}
              className={inputCls} placeholder="Sets" />
            <Input type="number" min={0} value={block.reps ?? ""} onChange={(e) => onChange({ reps: e.target.value ? Number(e.target.value) : null })}
              className={inputCls} placeholder="Reps" />
            <Input type="number" min={0} value={block.duration_sec ?? ""} onChange={(e) => onChange({ duration_sec: e.target.value ? Number(e.target.value) : null })}
              className={inputCls} placeholder="Duration (s)" />
          </div>
          <Textarea value={block.description || ""} onChange={(e) => onChange({ description: e.target.value })}
            className={`min-h-[50px] ${inputCls}`} placeholder="Description / cues" />
          <Input value={block.video_url || ""} onChange={(e) => onChange({ video_url: e.target.value })}
            className={inputCls} placeholder="YouTube embed URL (optional)" />
          <div className="flex flex-wrap gap-1.5">
            {TOOLS.map((t) => {
              const on = (block.tool_tags || []).includes(t);
              return (
                <button key={t} type="button"
                  onClick={() => onChange({ tool_tags: on ? block.tool_tags.filter((x) => x !== t) : [...(block.tool_tags || []), t] })}
                  className={`text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full border ${on ? "bg-cyan-400/20 text-cyan-200 border-cyan-400/50" : "bg-white/5 text-white/55 border-white/10 hover:border-white/20"}`}
                  data-testid={`block-${idx}-tool-${t}`}>
                  {TOOL_LABELS[t]}
                </button>
              );
            })}
          </div>
        </div>
        {/* Remove */}
        <button onClick={onRemove} className="text-white/40 hover:text-red-400 self-start" data-testid={`block-${idx}-remove`}>
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
